import { FormFieldComponent } from '../components/FormComponentTypes';
import { UISchema } from './jsonschemaTypes';
import dfsSearch from './dfsSearch';


/**
 * Traverses a UI Schema to extract embedded React component fields and replace them with string keys.
 * 
 * This utility is particularly useful when serializing UI Schemas that contain functional 
 * components (which cannot be natively stringified). It uses a Depth-First Search (DFS) 
 * to find all `ui:field` properties.
 *
 * @param uiSchema - The UI Schema object to process.
 * @returns An object mapping generated unique keys to their original `FormFieldComponent` functions.
 * 
 * @example
 * const embedded = collectEmbeddedUIFieldsFromUISchema(myUISchema);
 * // myUISchema['ui:field'] is now "EmbeddedField_0_MyComponent"
 * // embedded["EmbeddedField_0_MyComponent"] is the original function.
 */
function collectEmbeddedUIFieldsFromUISchema(uiSchema: UISchema<any>) {
  let counter = 0;
  const embeddedFields: Record<string, FormFieldComponent<any>> = {};
  const embeddedFieldKeyMap = new WeakMap<Function, string>();

  if (!uiSchema || typeof uiSchema !== "object") {
    return embeddedFields;
  }


  dfsSearch([uiSchema], (current, { stack }) => {
    Object.getOwnPropertyNames(current).forEach(attr => {
      const value = current[attr];
      if (attr === "ui:field") {
        if (typeof value === "function") {
          if (!embeddedFieldKeyMap.has(value)) {
            const fieldKey = `EmbeddedField_${counter++}_${value.name}`;
            embeddedFieldKeyMap.set(value, fieldKey);
            embeddedFields[fieldKey] = value;
          }
          current[attr] = embeddedFieldKeyMap.get(value);
        }
      } else if (typeof value === "object" && value) {
        stack.push(value);
      }
    });
  });

  return embeddedFields;
}


export default collectEmbeddedUIFieldsFromUISchema;
